import React from 'react';
import { Plus } from 'lucide-react';

/**
 * AddPlaceholder - Standardized "Add" button component
 *
 * Props:
 * - label: string
 * - sublabel?: string
 * - onClick: () => void
 * - disabled?: boolean
 * - variant?: 'default' | 'link'
 * - className?: string
 */
export const AddPlaceholder = ({
  label,
  sublabel,
  onClick,
  disabled = false,
  variant = 'default',
  className = '',
}) => {
  // LINK variant - subtle, text-based, left-aligned
  if (variant === 'link') {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`
          group
          flex items-center gap-2
          py-2
          transition-colors duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${className}
        `.trim()}
      >
        {/* Text with underline */}
        <span
          className={`
            font-['Roboto:Regular',sans-serif] text-[14px]
            underline underline-offset-2
            ${disabled ? 'text-gray-400' : 'text-gray-600 group-hover:text-[#61b8e7]'}
            transition-colors duration-200
          `.trim()}
        >
          {label}
        </span>

        {/* Small + icon on the right */}
        <Plus
          className={`
            w-[14px] h-[14px]
            ${disabled ? 'text-gray-400' : 'text-gray-600 group-hover:text-[#61b8e7]'}
            transition-colors duration-200
          `.trim()}
          strokeWidth={2.5}
        />
      </button>
    );
  }

  // DEFAULT variant - full button style
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        w-full min-h-[56px] px-4 py-3
        bg-white border border-[#61b8e7] rounded-lg
        flex items-center justify-center gap-3
        group
        transition-all duration-200
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#f0f9ff] hover:shadow-sm cursor-pointer'}
        ${className}
      `.trim()}
    >
      {/* Icon */}
      <div
        className={`
          bg-[#61b8e7] rounded-[6px] w-[32px] h-[32px]
          flex items-center justify-center
          transition-transform duration-200
          ${!disabled ? 'group-hover:scale-110' : ''}
        `.trim()}
      >
        <Plus className="w-[18px] h-[18px] text-white" strokeWidth={3} />
      </div>

      {/* Text */}
      <div className="flex items-baseline gap-1.5">
        <span
          className={`
            font-['Roboto:Medium',sans-serif] font-medium text-[15px]
            ${disabled ? 'text-gray-400' : 'text-[#61b8e7] group-hover:text-[#4da3d1]'}
            transition-colors duration-200
          `.trim()}
          style={{ fontVariationSettings: "'wdth' 100" }}
        >
          {label}
        </span>

        {sublabel && (
          <span
            className={`
              font-['Roboto:Regular',sans-serif] text-[14px]
              ${disabled ? 'text-gray-400' : 'text-gray-500'}
            `.trim()}
          >
            {sublabel}
          </span>
        )}
      </div>
    </button>
  );
};
